<template>
  <div class="ugc-page">
    <div class="ugc-header">
      <h1 class="ugc-title">UGC Upload</h1>
    </div>

    <div class="ugc-top-row">
      <div class="ugc-card ugc-form-card">
        <div v-if="error" class="ugc-alert ugc-alert--error">
          <i class="fal fa-exclamation-circle"></i> {{ error }}
        </div>
        <div v-if="successMessage" class="ugc-alert ugc-alert--success">
          <i class="fal fa-check-circle"></i> {{ successMessage }}
        </div>

        <div class="ugc-field">
          <label class="ugc-label">Select business</label>
          <select v-model="form.business_id" class="ugc-select" :disabled="loadingBusinesses">
            <option :value="null" disabled>{{ loadingBusinesses ? 'Loading…' : 'Select business' }}</option>
            <option v-for="item in businesses" :key="item.id" :value="item.id">
              {{ item.display_name || item.company || `Business #${item.id}` }}
            </option>
          </select>
        </div>

        <div class="ugc-field">
          <label class="ugc-label">Select file</label>
          <label class="ugc-file-picker">
            <input type="file" class="ugc-file-picker-input" accept="video/mp4,video/quicktime,video/x-msvideo,video/x-ms-wmv,video/webm" @change="onFileChange" />
            <i class="fal fa-upload"></i>
            <span v-if="form.file">{{ form.file.name }} <span class="ugc-file-size">({{ formatBytes(form.file.size) }})</span></span>
            <span v-else class="ugc-file-placeholder">Click to select a file</span>
          </label>
          <div class="ugc-hint">MP4, MOV, AVI, WMV, WebM — max recommended size: 100MB</div>
        </div>

        <div class="ugc-field">
          <label class="ugc-label">UGC price excl. VAT (DKK)</label>
          <div class="ugc-price-wrap">
            <span class="ugc-price-prefix">DKK</span>
            <input v-model.number="form.ugc_price" type="number" min="0" class="ugc-input ugc-input--price" />
          </div>
        </div>

        <div class="ugc-field">
          <label class="ugc-label">Rights — Where can the content be used?</label>
          <div class="ugc-checkbox-grid">
            <label class="ugc-checkbox-row">
              <input type="checkbox" value="social_media" v-model="form.usage_rights" />
              <span>Social media (organic)</span>
            </label>
            <label class="ugc-checkbox-row">
              <input type="checkbox" value="paid_ads" v-model="form.usage_rights" />
              <span>Paid ads / advertising</span>
            </label>
            <label class="ugc-checkbox-row">
              <input type="checkbox" value="website" v-model="form.usage_rights" />
              <span>Website / webshop</span>
            </label>
            <label class="ugc-checkbox-row">
              <input type="checkbox" value="email_marketing" v-model="form.usage_rights" />
              <span>Email marketing</span>
            </label>
            <label class="ugc-checkbox-row">
              <input type="checkbox" value="landing_pages" v-model="form.usage_rights" />
              <span>Landing pages</span>
            </label>
            <label class="ugc-checkbox-row">
              <input type="checkbox" value="print_outdoor" v-model="form.usage_rights" />
              <span>Print / outdoor (premium)</span>
            </label>
          </div>
        </div>

        <div class="ugc-field">
          <label class="ugc-label">Rights period</label>
          <select v-model="form.rights_period" class="ugc-select">
            <option v-for="opt in rightsPeriodOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
          </select>
        </div>

        <label class="ugc-checkbox-row ugc-field">
          <input type="checkbox" v-model="form.buyout" />
          <span>Would you like to offer buyout/full rights?</span>
        </label>

        <div class="ugc-field">
          <label class="ugc-label">Extra requirements / terms</label>
          <textarea
            v-model="form.extra_terms"
            class="ugc-textarea"
            rows="2"
            placeholder="E.g.: May not be edited without approval, must be tagged for organic use..."
          ></textarea>
        </div>

        <button
          class="ugc-btn ugc-btn--primary"
          :disabled="uploading || !form.file || !form.business_id"
          @click="uploadFile"
        >
          {{ uploading ? 'Uploading…' : 'Upload' }}
        </button>
      </div>

      <div class="ugc-card ugc-faq-card">
        <button class="ugc-faq-toggle" @click="faqOpen = !faqOpen">
          <span>When do I receive payment for my UGC?</span>
          <i class="fal" :class="faqOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </button>
        <div v-if="faqOpen" class="ugc-faq-body">
          <p>Once the business has purchased your UGC content, payment is processed automatically. You'll usually receive payment within 3–4 business days after the purchase.</p>
          <p class="mb-0">In some cases it may take a little longer depending on your bank and payment processing times.</p>
        </div>
      </div>
    </div>

    <div class="ugc-main-card">
      <div class="ugc-section-header">
        <div class="ugc-section-header__left">
          <span class="ugc-section-title">My uploaded assets</span>
          <span v-if="assets.length" class="ugc-section-count">{{ assets.length }}</span>
        </div>
        <div class="ugc-section-header__right">
          <button class="ugc-refresh-btn" :class="{ 'ugc-refresh-btn--loading': loadingAssets }" @click="loadAssets">
            <i class="fal fa-sync-alt"></i> Refresh
          </button>
        </div>
      </div>

      <div class="ugc-section-body">
        <div v-if="assets.length" class="ugc-grid">
          <div v-for="asset in assets" :key="asset.id" class="ugc-asset-card">
            <div
              class="ugc-asset-thumb"
              :class="{ 'ugc-asset-thumb--clickable': canPreview(asset) }"
              :style="!asset.thumbnail_url ? assetPlaceholderStyle(asset.id) : null"
              @click="openPreview(asset)"
            >
              <img v-if="asset.thumbnail_url" :src="asset.thumbnail_url" class="ugc-asset-thumb-img" alt="" />
              <i
                v-if="!asset.thumbnail_url"
                class="fal"
                :class="asset.mime_type && asset.mime_type.startsWith('video') ? 'fa-play-circle' : 'fa-image'"
              ></i>
              <i
                v-else-if="asset.mime_type && asset.mime_type.startsWith('video')"
                class="fal fa-play-circle ugc-asset-play-icon"
              ></i>
              <span class="ugc-asset-status-badge" :class="'ugc-status--' + statusColor(asset)">{{ formatStatusLabel(asset) }}</span>
            </div>
            <div class="ugc-asset-body">
              <div class="ugc-asset-filename" :title="asset.original_filename">{{ asset.original_filename }}</div>
              <div class="ugc-asset-meta">
                <span>
                  {{
                    asset.business && asset.business.display_name
                      ? asset.business.display_name
                      : asset.business && asset.business.company
                        ? asset.business.company
                        : 'Business #' + asset.business_id
                  }}
                </span>
                <span class="ugc-asset-price">{{ formatPrice(asset.ugc_price) }}</span>
              </div>
              <div class="ugc-asset-submeta">
                <span>{{ formatRightsPeriod(asset.rights_period) }}</span>
                <span v-if="asset.usage_rights && asset.usage_rights.length"> · {{ formatUsageRights(asset.usage_rights) }}</span>
              </div>
              <div class="ugc-asset-submeta">
                <span>{{ formatBytes(asset.file_size) }}</span>
                <span> · Uploaded {{ formatDate(asset.stored_at) }}</span>
              </div>
              <span class="ugc-pill" :class="asset.is_purchased ? 'ugc-status--success' : 'ugc-status--grey'">
                {{ asset.is_purchased ? 'Sold' : 'Not sold yet' }}
              </span>
            </div>
          </div>
        </div>
        <div v-else class="ugc-empty">No uploaded assets yet.</div>
      </div>
    </div>

    <v-dialog v-model="showPreviewDialog" max-width="900" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ previewAsset ? previewAsset.original_filename : 'Preview' }}</span>
          <v-btn icon @click="closePreview"><v-icon>fal fa-times</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="previewLoading" class="text-center py-8">Loading preview...</div>
          <div v-else-if="previewUrl" class="ugc-preview-wrapper">
            <video ref="previewVideo" :src="previewUrl" controls autoplay playsinline class="ugc-preview-video"></video>
          </div>
          <div v-else class="text--secondary">No preview available.</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

const ASSET_COLORS = ['#f4b8ab', '#b8d4f4', '#b8f4c8', '#f4e8b8', '#d4b8f4'];

export default {
  name: "UgcUpload",

  data() {
    return {
      uploading: false,
      thumbnailBlob: null,
      loadingAssets: false,
      loadingBusinesses: false,
      faqOpen: false,
      error: "",
      successMessage: "",
      showPreviewDialog: false,
      previewUrl: "",
      previewAsset: null,
      previewLoading: false,
      assets: [],
      businesses: [],
      rightsPeriodOptions: [
        { text: '30 days',    value: '30' },
        { text: '90 days',    value: '90' },
        { text: '6 months',   value: '180' },
        { text: '12 months',  value: '365' },
        { text: 'Unlimited',  value: 'unlimited' },
      ],
      form: {
        business_id: null,
        file: null,
        ugc_price: null,
        usage_rights: [],
        rights_period: null,
        buyout: false,
        extra_terms: '',
      },
    };
  },

  computed: {
    ...mapState("core/auth", ["user"]),

    influencerId() {
      return this.user?.user_id;
    },
  },

  created() {
    this.loadInitialData();
  },

  methods: {
    ...mapActions("influencer", [
      "startManualUgcUpload",
      "completeManualUgcUpload",
      "getInfluencerUgcAssets",
      "getInfluencerUgcBusinesses",
      "previewInfluencerUgcAsset",
    ]),

    canPreview(asset) {
      return asset.status_label === 'ready' && asset.mime_type && asset.mime_type.startsWith('video');
    },
    async openPreview(asset) {
      if (!this.canPreview(asset)) return;
      this.previewLoading = true;
      this.previewUrl = '';
      this.previewAsset = asset;
      this.showPreviewDialog = true;
      this.error = '';
      try {
        const r = await this.previewInfluencerUgcAsset({ ugc_asset_id: asset.id });
        this.previewUrl = r.url;
      } catch (e) {
        this.error = e?.response?.data?.message || e?.message || 'Could not load preview.';
        this.showPreviewDialog = false;
      } finally {
        this.previewLoading = false;
      }
    },
    closePreview() {
      const v = this.$refs.previewVideo;
      if (v) v.pause();
      this.showPreviewDialog = false;
      this.previewUrl = '';
      this.previewAsset = null;
    },

    async onFileChange(event) {
      const file = event.target.files[0] || null;
      this.form.file = file;
      this.thumbnailBlob = null;

      if (file && file.type.startsWith("video/")) {
        try {
          this.thumbnailBlob = await this.captureVideoThumbnail(file);
        } catch (e) {
          this.thumbnailBlob = null; // no thumbnail, upload still proceeds normally
        }
      }
    },

    captureVideoThumbnail(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;
        video.style.display = "none";
        document.body.appendChild(video);

        const cleanup = () => {
          URL.revokeObjectURL(url);
          video.remove();
        };

        video.addEventListener("loadedmetadata", () => {
          video.currentTime = Math.min(1, (video.duration || 1) / 2);
        });

        video.addEventListener("seeked", () => {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          if (!canvas.width || !canvas.height) {
            cleanup();
            reject(new Error("Video has no visual dimensions."));
            return;
          }

          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            blob => {
              cleanup();
              blob ? resolve(blob) : reject(new Error("Could not capture frame."));
            },
            "image/jpeg",
            0.8
          );
        });

        video.addEventListener("error", () => {
          cleanup();
          reject(new Error("Browser could not decode this video."));
        });

        video.src = url;
      });
    },

    async loadInitialData() {
      await Promise.all([
        this.loadAssets(),
        this.loadBusinesses(),
      ]);
    },

    async loadBusinesses() {
      try {
        this.loadingBusinesses = true;
        this.businesses = await this.getInfluencerUgcBusinesses();
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Could not load available businesses.";
      } finally {
        this.loadingBusinesses = false;
      }
    },

    async uploadFile() {
      this.error = "";
      this.successMessage = "";

      if (!this.form.file) {
        this.error = "Please select a file.";
        return;
      }

      if (!this.form.business_id) {
        this.error = "Please select a business.";
        return;
      }

      try {
        this.uploading = true;

        const startResponse = await this.startManualUgcUpload({
          business_id: this.form.business_id,
          filename: this.form.file.name,
          mime_type: this.form.file.type,
          ugc_price: this.form.ugc_price || null,
          usage_rights: this.form.usage_rights.length ? this.form.usage_rights : null,
          rights_period: this.form.rights_period || null,
          buyout: this.form.buyout,
          extra_terms: this.form.extra_terms || null,
        });

        const uploads = [
          fetch(startResponse.upload_url, {
            method: "PUT",
            headers: {
              "Content-Type": this.form.file.type,
            },
            body: this.form.file,
          }),
        ];

        const hasThumbnail = !!(this.thumbnailBlob && startResponse.thumbnail_upload_url);

        if (hasThumbnail) {
          uploads.push(
            fetch(startResponse.thumbnail_upload_url, {
              method: "PUT",
              headers: {
                "Content-Type": "image/jpeg",
              },
              body: this.thumbnailBlob,
            })
          );
        }

        const [uploadResponse, thumbUploadResponse] = await Promise.all(uploads);

        if (!uploadResponse.ok) {
          throw new Error("Upload to storage failed.");
        }

        await this.completeManualUgcUpload({
          ugc_asset_id: startResponse.ugc_asset.id,
          has_thumbnail: hasThumbnail && !!thumbUploadResponse?.ok,
        });

        this.successMessage = "The file was uploaded.";
        this.form.file = null;
        this.thumbnailBlob = null;

        await this.loadAssets();
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Upload failed.";
      } finally {
        this.uploading = false;
      }
    },

    async loadAssets() {
      try {
        this.loadingAssets = true;
        this.assets = await this.getInfluencerUgcAssets();
      } catch (error) {
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          "Could not load uploaded assets.";
      } finally {
        this.loadingAssets = false;
      }
    },

    assetPlaceholderStyle(id) {
      const color = ASSET_COLORS[Number(id) % ASSET_COLORS.length];
      return { background: color };
    },

    statusColor(asset) {
      const label = asset.status_label;
      if (asset.is_purchased) return "success";
      if (label === "ready") return "info";
      if (label === "pending_upload") return "grey";
      if (label === "failed") return "error";
      if (label === "deleted") return "grey";
      return "grey";
    },

    formatStatusLabel(asset) {
      const label = asset.status_label;

      if (!label) {
        return asset.status;
      }

      if (label === "pending_upload") return "Pending upload";
      if (label === "ready") return "Uploaded";
      if (label === "failed") return "Failed";
      if (label === "deleted") return "Deleted";

      return label;
    },

    formatDate(value) {
      if (!value) return "-";

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    },

    formatBytes(value) {
      if (!value) return "-";

      const sizes = ["B", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(value) / Math.log(1024));
      return `${(value / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
    },

    formatPrice(value) {
      if (value === null || value === undefined) return "-";
      return `DKK ${(value / 100).toFixed(2)}`;
    },

    formatRightsPeriod(value) {
      const map = {
        "30": "30 days",
        "90": "90 days",
        "180": "6 months",
        "365": "12 months",
        "unlimited": "Unlimited",
      };
      return map[value] || value || "-";
    },

    formatUsageRights(value) {
      if (!value || !value.length) return "-";
      const map = {
        social_media:    "Social media",
        paid_ads:        "Paid ads",
        website:         "Website",
        email_marketing: "Email marketing",
        landing_pages:   "Landing pages",
        print_outdoor:   "Print / outdoor",
      };
      return value.map(v => map[v] || v).join(", ");
    },
  },
};
</script>

<style scoped>
.ugc-page {
  max-width: var(--maxw);
  margin: 0 auto;
  padding: 24px 20px 40px;
}
.ugc-header { margin-bottom: 20px; }
.ugc-title { font-size: 22px; font-weight: 650; color: var(--ink); margin: 0; letter-spacing: -.02em; }

.ugc-top-row {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  align-items: start;
  margin-bottom: 16px;
}
@media (max-width: 900px) { .ugc-top-row { grid-template-columns: 1fr; } }

.ugc-card, .ugc-main-card {
  background: var(--surface, #fff);
  border: 1px solid var(--line, #eaecf0);
  border-radius: var(--r-lg, 14px);
  padding: 20px;
}

.ugc-alert {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: var(--r-ctrl, 8px);
  font-size: 13px;
  margin-bottom: 16px;
}
.ugc-alert--error { background: rgba(224,62,56,0.08); color: #e03e38; border: 1px solid rgba(224,62,56,0.2); }
.ugc-alert--success { background: rgba(29,184,122,0.08); color: #1db87a; border: 1px solid rgba(29,184,122,0.2); }

.ugc-field { margin-bottom: 14px; }
.ugc-label { display: block; font-size: 12.5px; font-weight: 600; color: var(--ink-2, #555); margin-bottom: 6px; }
.ugc-hint { font-size: 11px; color: var(--ink-3, #aaa); margin-top: 4px; }

.ugc-input, .ugc-select, .ugc-textarea {
  width: 100%;
  border: 1px solid var(--line, #e0e0e0);
  border-radius: var(--r-ctrl, 8px);
  font-size: 13px;
  color: var(--ink-1, #111);
  background: var(--surface, #fff);
  outline: none;
  box-sizing: border-box;
}
.ugc-input, .ugc-select { height: 38px; padding: 0 12px; }
.ugc-textarea { padding: 10px 12px; resize: vertical; font-family: inherit; }
.ugc-input:focus, .ugc-select:focus, .ugc-textarea:focus { border-color: var(--accent, #e05540); }
.ugc-select:disabled { opacity: .6; cursor: not-allowed; }

.ugc-price-wrap { display: flex; align-items: center; border: 1px solid var(--line, #e0e0e0); border-radius: var(--r-ctrl, 8px); overflow: hidden; }
.ugc-price-wrap:focus-within { border-color: var(--accent, #e05540); }
.ugc-price-prefix { padding: 0 10px; font-size: 12.5px; color: var(--ink-3, #aaa); border-right: 1px solid var(--line, #e0e0e0); height: 38px; display: flex; align-items: center; }
.ugc-input--price { border: none; height: 36px; }

.ugc-file-picker {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--line, #e0e0e0);
  border-radius: var(--r-ctrl, 8px);
  font-size: 13px;
  color: var(--ink-1, #111);
  cursor: pointer;
  position: relative;
}
.ugc-file-picker:hover { border-color: var(--accent, #e05540); }
.ugc-file-picker i { color: var(--ink-3, #aaa); font-size: 13px; }
.ugc-file-picker-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
.ugc-file-placeholder { color: var(--ink-3, #aaa); }
.ugc-file-size { color: var(--ink-3, #aaa); }

.ugc-checkbox-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
}
@media (max-width: 500px) { .ugc-checkbox-grid { grid-template-columns: 1fr; } }

.ugc-checkbox-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--ink-1, #111);
  cursor: pointer;
}
.ugc-checkbox-row input[type="checkbox"] {
  width: 15px;
  height: 15px;
  accent-color: var(--accent, #e05540);
  cursor: pointer;
}

.ugc-btn {
  height: 42px;
  padding: 0 24px;
  border: none;
  border-radius: var(--r-ctrl, 8px);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
}
.ugc-btn--primary { background: var(--accent, #e05540); color: #fff; }
.ugc-btn--primary:hover:not(:disabled) { opacity: .9; }
.ugc-btn:disabled { opacity: .5; cursor: not-allowed; }

.ugc-faq-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink-1, #111);
  cursor: pointer;
  text-align: left;
}
.ugc-faq-toggle i { color: var(--ink-3, #aaa); font-size: 13px; }
.ugc-faq-body { margin-top: 12px; font-size: 13px; color: var(--ink-2, #555); line-height: 1.6; }

.ugc-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.ugc-section-header__left { display: flex; align-items: center; gap: 8px; }
.ugc-section-title { font-size: 14px; font-weight: 600; color: var(--ink-1, #111); }
.ugc-section-count {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px;
  background: var(--surface-2, #f3f3f3); color: var(--ink-2, #555);
  font-size: 11px; font-weight: 700;
}

.ugc-refresh-btn {
  display: flex; align-items: center; gap: 6px;
  height: 32px; padding: 0 12px;
  border: 1px solid var(--line, #e0e0e0); border-radius: var(--r-ctrl, 8px);
  background: transparent; color: var(--ink-2, #555);
  font-size: 12.5px; font-weight: 600; cursor: pointer;
}
.ugc-refresh-btn:hover { background: var(--surface-2, #f7f7f7); }
.ugc-refresh-btn--loading i { animation: ugc-spin 1s linear infinite; }
@keyframes ugc-spin { to { transform: rotate(360deg); } }

.ugc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.ugc-asset-card {
  border: 1px solid var(--line, #eaecf0);
  border-radius: var(--r-lg, 14px);
  overflow: hidden;
  background: var(--surface, #fff);
}

.ugc-asset-thumb {
  position: relative;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  color: rgba(255,255,255,0.85);
}

.ugc-asset-thumb-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ugc-asset-play-icon {
  position: relative;
  z-index: 1;
  font-size: 32px;
  color: rgba(255, 255, 255, 0.92);
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.45);
}

.ugc-asset-status-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  color: #fff;
}
.ugc-status--success { background: #1db87a; }
.ugc-status--info { background: #4F9CF9; }
.ugc-status--warning { background: #F4A535; }
.ugc-status--error { background: #e03e38; }
.ugc-status--grey { background: #9aa0a6; }

.ugc-asset-body { padding: 12px 14px 14px; }
.ugc-asset-filename {
  font-size: 13px; font-weight: 600; color: var(--ink-1, #111);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-bottom: 4px;
}
.ugc-asset-meta {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px; color: var(--ink-3, #aaa); margin-bottom: 4px;
}
.ugc-asset-price { font-weight: 700; color: var(--ink-1, #111); }
.ugc-asset-submeta { font-size: 11px; color: var(--ink-3, #aaa); margin-bottom: 2px; }

.ugc-pill {
  display: inline-flex; align-items: center;
  height: 20px; padding: 0 8px; border-radius: 999px;
  font-size: 10.5px; font-weight: 700; color: #fff; margin-top: 8px;
}

.ugc-empty { padding: 32px; text-align: center; font-size: 13px; color: var(--ink-3, #aaa); }

.ugc-asset-thumb--clickable { cursor: pointer; }
.ugc-asset-thumb--clickable:hover .ugc-asset-play-icon { transform: scale(1.1); }
.ugc-asset-play-icon { transition: transform .15s; }

.ugc-preview-wrapper { position: relative; width: 100%; overflow: hidden; border-radius: 8px; background: #000; }
.ugc-preview-video { width: 100%; max-height: 70vh; display: block; }
</style>